import React from "react";
import { Box, CircularProgress, IconButton, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";

const THUMB_WIDTH = 64;

/**
 * Portrait clip tile for the Clip board (matches design reference).
 */
export default function ClipThumbnail({
  clip,
  index,
  onRemove,
  onRegenerate,
  regenerateDisabled = false,
  onDragStart,
  onDragOver,
  onDrop,
  isDragOver,
  overlap = false,
  generating = false,
}) {
  const theme = useTheme();
  const failed = clip?.status === "failed";
  const pending = clip?.status === "pending";
  const isGenerating = generating || clip?.status === "generating";
  const canRegenerate =
    typeof onRegenerate === "function" &&
    Number(clip?.segmentIndex) >= 1 &&
    !isGenerating;

  if (isGenerating) {
    return (
      <Box
        sx={{
          position: "relative",
          width: THUMB_WIDTH,
          flexShrink: 0,
          ml: overlap && index > 0 ? -1.5 : 0,
          borderRadius: 1.5,
          overflow: "hidden",
          border: `2px dashed ${alpha(theme.palette.primary.main, 0.45)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          height: 112,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
        }}
      >
        <CircularProgress size={22} thickness={5} />
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {clip.segmentIndex}/{clip.segmentTotal}
        </Typography>
      </Box>
    );
  }

  if (pending) {
    return (
      <Box
        sx={{
          position: "relative",
          width: THUMB_WIDTH,
          flexShrink: 0,
          ml: overlap && index > 0 ? -1.5 : 0,
          borderRadius: 1.5,
          overflow: "hidden",
          border: `2px dashed ${alpha(theme.palette.text.primary, 0.28)}`,
          bgcolor: alpha(theme.palette.action.hover, 0.04),
          height: 112,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.35,
          px: 0.5,
        }}
      >
        {onRemove ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(clip.id);
            }}
            sx={{
              position: "absolute",
              top: 2,
              right: 2,
              zIndex: 2,
              bgcolor: alpha("#fff", 0.92),
              color: "text.primary",
              width: 22,
              height: 22,
              p: 0,
              boxShadow: 1,
              "&:hover": { bgcolor: "#fff" },
            }}
            aria-label="Remove scene"
          >
            <DeleteOutline sx={{ fontSize: 14 }} />
          </IconButton>
        ) : null}
        <Typography variant="caption" fontWeight={800} color="text.secondary" lineHeight={1.1}>
          {clip.segmentIndex}/{clip.segmentTotal}
        </Typography>
        <Typography variant="caption" color="text.disabled" fontWeight={600} lineHeight={1.1}>
          Scene
        </Typography>
        {canRegenerate ? (
          <Tooltip title="Generate this scene">
            <span>
              <IconButton
                size="small"
                disabled={regenerateDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  onRegenerate(clip);
                }}
                sx={{
                  mt: 0.25,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main",
                  width: 28,
                  height: 28,
                  p: 0,
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.22) },
                }}
                aria-label="Generate scene"
              >
                <PlayArrowOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
      </Box>
    );
  }

  if (failed) {
    return (
      <Box
        sx={{
          position: "relative",
          width: THUMB_WIDTH,
          flexShrink: 0,
          ml: overlap && index > 0 ? -1.5 : 0,
          borderRadius: 1.5,
          overflow: "hidden",
          border: `2px solid ${alpha(theme.palette.error.main, 0.7)}`,
          bgcolor: alpha(theme.palette.error.main, 0.08),
          height: 112,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.35,
          px: 0.5,
        }}
      >
        {onRemove ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(clip.id);
            }}
            sx={{
              position: "absolute",
              top: 2,
              right: 2,
              zIndex: 2,
              bgcolor: alpha("#fff", 0.92),
              color: "text.primary",
              width: 22,
              height: 22,
              p: 0,
              boxShadow: 1,
              "&:hover": { bgcolor: "#fff" },
            }}
            aria-label="Remove clip"
          >
            <DeleteOutline sx={{ fontSize: 14 }} />
          </IconButton>
        ) : null}
        <ErrorOutline sx={{ color: "error.main", fontSize: 22 }} />
        <Typography variant="caption" fontWeight={800} color="error.main" lineHeight={1.1}>
          {clip.segmentIndex}/{clip.segmentTotal}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600} lineHeight={1.1}>
          Failed
        </Typography>
        {canRegenerate ? (
          <Tooltip title="Generate this scene again">
            <span>
              <IconButton
                size="small"
                disabled={regenerateDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  onRegenerate(clip);
                }}
                sx={{
                  mt: 0.25,
                  bgcolor: alpha(theme.palette.error.main, 0.12),
                  color: "error.main",
                  width: 26,
                  height: 26,
                  p: 0,
                  "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.22) },
                }}
                aria-label="Generate scene"
              >
                <PlayArrowOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
      </Box>
    );
  }

  return (
    <Box
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(index));
        onDragStart?.(index);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver?.(e, index);
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop?.(index);
      }}
      sx={{
        position: "relative",
        width: THUMB_WIDTH,
        flexShrink: 0,
        ml: overlap && index > 0 ? -1.5 : 0,
        borderRadius: 1.5,
        overflow: "hidden",
        border: `2px solid ${isDragOver ? theme.palette.primary.main : alpha(theme.palette.divider, 0.9)}`,
        boxShadow: isDragOver ? theme.shadows[4] : theme.shadows[1],
        cursor: "grab",
        bgcolor: "#000",
        transition: "border-color 0.15s, transform 0.15s",
        "&:active": { cursor: "grabbing" },
      }}
    >
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(clip.id);
        }}
        sx={{
          position: "absolute",
          top: 2,
          right: 2,
          zIndex: 2,
          bgcolor: alpha("#fff", 0.92),
          color: "text.primary",
          width: 22,
          height: 22,
          p: 0,
          boxShadow: 1,
          "&:hover": { bgcolor: "#fff" },
        }}
        aria-label="Remove clip"
      >
        <DeleteOutline sx={{ fontSize: 14 }} />
      </IconButton>

      {canRegenerate ? (
        <Tooltip title="Regenerate this scene">
          <span>
            <IconButton
              size="small"
              disabled={regenerateDisabled}
              onClick={(e) => {
                e.stopPropagation();
                onRegenerate(clip);
              }}
              sx={{
                position: "absolute",
                top: 2,
                left: 2,
                zIndex: 2,
                bgcolor: alpha("#fff", 0.92),
                color: "text.primary",
                width: 22,
                height: 22,
                p: 0,
                boxShadow: 1,
                "&:hover": { bgcolor: "#fff" },
                "&.Mui-disabled": { bgcolor: alpha("#fff", 0.6) },
              }}
              aria-label="Regenerate scene"
            >
              <RefreshOutlined sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
      ) : null}

      {clip.segmentIndex ? (
        <Box
          sx={{
            position: "absolute",
            bottom: 2,
            left: 2,
            zIndex: 2,
            px: 0.5,
            py: 0.1,
            borderRadius: 0.75,
            bgcolor: alpha("#000", 0.65),
            color: "#fff",
            fontSize: 10,
            fontWeight: 800,
            lineHeight: 1.3,
          }}
        >
          {clip.segmentIndex}
          {clip.segmentTotal ? `/${clip.segmentTotal}` : ""}
        </Box>
      ) : null}

      <Box
        component="video"
        src={clip.thumbnailUrl || clip.url}
        muted
        playsInline
        preload="metadata"
        sx={{
          width: "100%",
          height: 112,
          objectFit: "cover",
          display: "block",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
}

export function AddClipTile({ onClick, disabled, lang }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: 88,
        height: 112,
        flexShrink: 0,
        border: "2px dashed",
        borderColor: disabled ? "action.disabled" : "divider",
        borderRadius: 1.5,
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        cursor: disabled ? "not-allowed" : "pointer",
        color: disabled ? "text.disabled" : "text.secondary",
        transition: "border-color 0.15s, color 0.15s",
        "&:hover": disabled
          ? {}
          : {
              borderColor: "primary.main",
              color: "primary.main",
            },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          border: "2px solid currentColor",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
          fontWeight: 300,
          lineHeight: 1,
        }}
      >
        +
      </Box>
      <Box component="span" sx={{ fontSize: 13, fontWeight: 700 }}>
        {lang?.videoStitchAdd || "Add"}
      </Box>
    </Box>
  );
}
